import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useCreateVariant, useProducts, useProduct } from '../../hooks/useProducts';
import { CreateProductModal } from '@modules/products';
import { Button, Modal, ControlledSelect } from '@shared/ui';
import { useIsMobile } from '@shared/hooks';
import { useForm } from 'react-hook-form';

interface AddVariantModalProps {
    isOpen: boolean;
    onClose: () => void;
    sourceProductId: number;
    onSuccess?: () => void;
}

interface VariantFormData {
    sourceProductId: number | null;
    selectedProductId: number | null;
}

export default function AddVariantModal({
    isOpen,
    onClose,
    sourceProductId,
    onSuccess,
}: AddVariantModalProps) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [variantMode, setVariantMode] = useState<'create' | 'select' | null>(null);
    const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);

    const { data: allProducts = [] } = useProducts();
    const { data: sourceProduct } = useProduct(sourceProductId);
    const createVariantMutation = useCreateVariant();

    const { handleSubmit, formState: { errors }, reset, control } = useForm<VariantFormData>({
        defaultValues: {
            sourceProductId: null,
            selectedProductId: null,
        },
    });

    useEffect(() => {
        if (isOpen) {
            reset({
                sourceProductId: sourceProductId,
                selectedProductId: null,
            });
        }
    }, [isOpen, sourceProductId, reset]);

    const handleClose = () => {
        setVariantMode(null);
        reset({
            sourceProductId: null,
            selectedProductId: null,
        });
        onClose();
    };

    const handleCreateNew = () => {
        if (isMobile) {
            setVariantMode(null);
            onClose();
            navigate(`/products/new?variantOf=${sourceProductId}`, {
                state: { from: `/products/${sourceProductId}` },
            });
            return;
        }
        setVariantMode(null);
        setIsCreateProductModalOpen(true);
    };

    const handleCreateProductSuccess = () => {
        setIsCreateProductModalOpen(false);
        setVariantMode(null);
        reset({
            sourceProductId: sourceProductId,
            selectedProductId: null,
        });
        onSuccess?.();
        onClose();
    };

    const handleSelectExisting = async (data: VariantFormData) => {
        if (!data.selectedProductId) {
            notifications.show({
                title: 'Ошибка',
                message: 'Выберите товар',
                color: 'red',
            });
            return;
        }
        try {
            await createVariantMutation.mutateAsync({
                products: [
                    {
                        productId: sourceProductId,
                    },
                    {
                        productId: data.selectedProductId,
                    },
                ],
            });
            notifications.show({
                title: 'Успешно',
                message: 'Вариант успешно создан',
                color: 'green',
            });
            handleClose();
            onSuccess?.();
        } catch (error: any) {
            notifications.show({
                title: 'Ошибка',
                message: error?.response?.data?.message || 'Ошибка при создании варианта',
                color: 'red',
            });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            size={variantMode === null ? 300 : 400}
            onClose={handleClose}
            title="Добавить вариант"
        >
            {variantMode === null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <Button
                        onClick={handleCreateNew}
                        style={{ width: '100%' }}
                    >
                        Создать новый товар
                    </Button>
                    <Button
                        onClick={() => setVariantMode('select')}
                        style={{ width: '100%' }}
                    >
                        Выбрать из существующих
                    </Button>
                </div>
            ) : (
                <form onSubmit={handleSubmit(handleSelectExisting)} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: 600, fontSize: '0.875rem' }}>
                            Текущий товар:
                        </label>
                        <ControlledSelect
                            name="sourceProductId"
                            control={control}
                            disabled
                            data={sourceProduct ? [{ value: sourceProduct.id.toString(), label: `${sourceProduct.name} (ID: ${sourceProduct.id})` }] : [{ value: sourceProductId.toString(), label: `Товар ID: ${sourceProductId}` }]}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: 600, fontSize: '0.875rem' }}>
                            Выберите товар для связи:
                        </label>
                        <ControlledSelect
                            name="selectedProductId"
                            control={control}
                            placeholder="-- Выберите товар --"
                            searchable
                            data={allProducts
                                .filter(p => p.id !== sourceProductId)
                                .map((p) => ({
                                    value: p.id.toString(),
                                    label: `${p.name} (ID: ${p.id})`
                                }))}
                            error={errors.selectedProductId}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <Button
                            type="button"
                            onClick={() => setVariantMode(null)}
                            variant='secondary'
                        >
                            Назад
                        </Button>
                        <Button
                            type="submit"
                            disabled={createVariantMutation.isPending}
                            loading={createVariantMutation.isPending}
                        >
                            Создать вариант
                        </Button>

                    </div>
                </form>
            )}

            <CreateProductModal
                isOpen={isCreateProductModalOpen}
                onClose={() => {
                    setIsCreateProductModalOpen(false);
                    setVariantMode(null);
                }}
                variantOfProductId={sourceProductId}
                onSuccess={handleCreateProductSuccess}
            />
        </Modal>
    );
}

